<template>
    <div>
        <HeaderComponent />
        <GlobalLoaderComponent v-if="global.loader.isLoading" />
        <SubmissionErrorsModalComponent v-if="global.submission_errors.show" />

        <main class="mt-5 p-6">
            <keep-alive>
                <router-view></router-view>
            </keep-alive>
        </main>

        <div v-if="currentRouteName == 'create-account'" class="w-full">
            <StepNavigationComponent />
        </div>

        <transition
            enter-active-class="transition ease-out duration-300 transform"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-out duration-300 transform"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="showTermsModal && !showEmailNotVerifiedModal"
                class="absolute top-0 left-0 z-40 w-full min-h-full bg-deep-blue"
            >
                <div class="m-auto max-w-screen-md">
                    <div class="p-5 pb-2">
                        <h2 class="text-center font-semibold text-xl mt-6">
                            Important notice regarding our use of your personal data.
                        </h2>
                    </div>
                    <div class="p-5">
                        <ValidationObserver v-slot="{ handleSubmit }">
                            <form @submit.prevent="handleSubmit(doHandleAgreeAndContinue)">
                                <p>
                                    Our Platform Privacy Policy explains the ways in which we intend to process the data
                                    you provide through this online account application platform, including special
                                    personal data as described in the policy. Some of these processing activities do
                                    need your consent. You do not have to give consent and opening an account with us is
                                    not conditional on you giving consent to these processing activities. You can
                                    withdraw your consent at any time by emailing us at
                                    <a href="mailto:info@amtfutures.co.uk">info@amtfutures.co.uk</a>
                                    .
                                </p>

                                <div class="mb-6">
                                    <CheckboxComponent
                                        id="accept_personal_data_processing"
                                        name="accept_personal_data_processing"
                                        label='I agree to the processing of my special personal data as described in the <a href="https://optionsdesk.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Platform Privacy Policy</a> for the purposes described in the policy.'
                                        v-model="acceptPersonalDataProcessing"
                                    />
                                </div>

                                <ButtonComponent
                                    id="agree-to-terms-button"
                                    class="mb-6"
                                    type="submit"
                                    color="primary"
                                    label="I agree &amp; continue"
                                />

                                <ButtonComponent
                                    color="secondary"
                                    label="Decline &amp; close"
                                    @click.native="doHandleDeclineAndClose()"
                                />
                            </form>
                        </ValidationObserver>
                    </div>
                </div>
            </div>
        </transition>
        <div v-show="showEmailNotVerifiedModal" class="absolute top-0 left-0 z-40 w-full min-h-full bg-deep-blue">
            <div class="m-auto max-w-screen-md">
                <div class="text-center">
                    <div class="logo-device text-center mb-6 mt-8">
                        <img
                            :src="global.app.url + 'images/logo-device.svg'"
                            alt="Options Desk O logo"
                            title="Options Desk"
                        />
                    </div>

                    <h1 class="text-xl uppercase font-semibold mb-5">Please check your email</h1>

                    <p>
                        In order to continue please verify your email address by checking the email we have just sent to
                        {{ user.email ? user.email : "you" }} and following the instructions. Please check your
                        Junk/Spam folder if you do not receive the email.
                    </p>
                    <p>
                        <span
                            class="cursor-pointer text-green underline"
                            @click="doResendEmailVerificationEmail"
                            v-if="!isSendingEmailVerificationEmail"
                        >
                            Resend Verification Email
                        </span>
                        <span v-else class="cursor-disabled">Resending Verification Email...</span>
                        <span v-if="isEmailVerificationEmailSent"><br>Email Resent!</span>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationObserver } from "vee-validate";

import HeaderComponent from "@components/partials/HeaderComponent";
import GlobalLoaderComponent from "@components/partials/GlobalLoaderComponent";
import StepNavigationComponent from "@components/partials/StepNavigationComponent";
import SubmissionErrorsModalComponent from "@components/partials/SubmissionErrorsModalComponent";

export default {
    name: "App",
    props: {},
    components: {
        ValidationObserver,
        HeaderComponent,
        GlobalLoaderComponent,
        StepNavigationComponent,
        SubmissionErrorsModalComponent,
    },
    computed: {
        ...mapGetters(["global", "user"]),
        currentRouteName() {
            return this.$route.name;
        },
        showTermsModal() {
            if (this.$route.name == "create-account") {
                return this.user.id && !this.user.accept_personal_data_processing ? true : false;
            }
        },
        showEmailNotVerifiedModal() {
            if (this.$route.name == "create-account") {
                return this.user.id && !this.user.email_verified_at ? true : false;
            }
        },
    },
    data: function () {
        return {
            acceptPersonalDataProcessing: false,
            isSendingEmailVerificationEmail: false,
            isEmailVerificationEmailSent: false
        };
    },
    methods: {
        ...mapActions(["doGetUser", "doUserAcceptPrivacyPolicy", "resetGlobalFlashResponse", "doLogout", "resendEmailVerificationEmail"]),
        getUserFromSession() {
            if (this.user.access_token && this.user.id && this.$route.name != "email-not-verified") {
                // If the previous access token has persisted in the Vuex store, let's set that token as the bearer token in Axios
                // alert("do call user endpoint");
                // @todo: call the user endpoint to ensure that the current user's details are up to date
                this.doGetUser(this.user.id);
            }
        },
        setAxiosBearerToken() {
            if (this.user.access_token) {
                axios.defaults.headers.common["Authorization"] = "Bearer " + this.user.access_token;
            }
        },
        doHandleAgreeAndContinue() {
            this.doUserAcceptPrivacyPolicy();
        },
        doHandleDeclineAndClose() {
            this.doLogout().then((response) => {
                this.$router.push({ name: "login" });
            });
        },
        doResendEmailVerificationEmail() {
            this.isSendingEmailVerificationEmail = true;
            this.isEmailVerificationEmailSent = false;
            this.resendEmailVerificationEmail().then((response) => {
                this.isSendingEmailVerificationEmail = false;
                this.isEmailVerificationEmailSent = true;
            });
        },
    },
    beforeMount() {
        this.setAxiosBearerToken(); // Set the bearer token in Axios
        this.getUserFromSession();
    },
    watch: {
        "$route.name"() {
            this.resetGlobalFlashResponse(); // Reset the flash messages every time we change page/route names
        },
    },
};
</script>
